import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Col,
    FormGroup,
    Input,
    Label,
    Modal,
    Progress,
    Row,
    Table,
} from "reactstrap";
import BetterBreadcrumb from 'src/components/Common/BetterBreadCrumb';
import DeleteInformationModal from 'src/components/DeleteInformation/DeleteInformationModal';
import { config } from 'src/config';
import { COMPLETED_TRAINING_WORKSHOPS } from 'src/constants/completedTrainingWorkshops';
import {
    useCreateCompletedTrainingWorkshopMutation,
    useDeleteCompletedTrainingWorkshopMutation,
    useGetCompletedTrainingWorkshopsQuery,
    useGetTraineeDashboardInfoQuery,
} from 'src/rtk/features/traineeDashboard/traineeDashboardApi';
import loader from '../../../assets/images/loader.gif';
import { CoreTrainingCertificate } from './CoreTrainingCertificate';
import { NotificationPdf } from './NotificationPdf';

const PreviousCompletedTrainingWorkshop = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [workshopCode, setWorkshopCode] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [document, setDocument] = useState<File | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [workshopIdToDelete, setWorkshopIdToDelete] = useState('');
    const [createCompletedTrainingWorkshop, { isLoading }] =
        useCreateCompletedTrainingWorkshopMutation();
    const [deleteCompletedTrainingWorkshop] =
        useDeleteCompletedTrainingWorkshopMutation();
    const {
        data: completedWorkshopsResponse,
        isLoading: isWorkshopListLoading,
        isError: isWorkshopListError,
    } = useGetCompletedTrainingWorkshopsQuery(undefined, {
        skip: !isModalOpen,
    });

    const completedWorkshopsData = completedWorkshopsResponse?.data;
    const completedWorkshops = Array.isArray(completedWorkshopsData)
        ? completedWorkshopsData
        : Array.isArray(completedWorkshopsData?.data)
            ? completedWorkshopsData.data
            : [];

    const resetForm = () => {
        setWorkshopCode('');
        setStartDate('');
        setEndDate('');
        setDocument(null);
        setFileInputKey((key) => key + 1);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        resetForm();
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const workshop = COMPLETED_TRAINING_WORKSHOPS.find(
            (item) => item.code === workshopCode
        );

        if (!workshop || !startDate || !endDate || !document) {
            toast.error('Training/Workshop Name, Start Date, End Date and Document are required.');
            return;
        }

        if (endDate < startDate) {
            toast.error('End Date cannot be earlier than Start Date.');
            return;
        }

        const formData = new FormData();
        formData.append('workshop_code', workshop.code);
        formData.append('training_workshop_name', workshop.name);
        formData.append('start_date', startDate);
        formData.append('end_date', endDate);
        formData.append('document', document);

        try {
            const response = await createCompletedTrainingWorkshop(formData).unwrap();
            toast.success(response?.message || 'Training/workshop uploaded successfully.');
            closeModal();
        } catch (error: any) {
            toast.error(error?.message || 'Unable to upload the training/workshop.');
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const response = await deleteCompletedTrainingWorkshop(id).unwrap();
            toast.success(response?.message || 'Training/workshop deleted successfully.');
            setWorkshopIdToDelete('');
        } catch (error: any) {
            toast.error(error?.data?.message || error?.message || 'Unable to delete the training/workshop.');
        }
    };

    const getDocumentUrl = (path: string) =>
        /^https?:\/\//i.test(path) ? path : `${config.APP_URL || ''}${path}`;

    return (
        <>
            <Row>
                <Col md='12'>
                    <Card>
                        <CardBody className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3'>
                            <div>
                                <CardTitle className='h5 mb-1'>
                                    <i className='fas fa-certificate me-2'></i>
                                    Previous Completed Training Workshop
                                </CardTitle>
                                <p className='text-muted mb-0'>
                                    If you passed FCPS Part 1 in 2025 or after and have not completed
                                    any workshop in eLogbook, please upload it here.
                                </p>
                            </div>
                            <Button color='primary' onClick={() => setIsModalOpen(true)}>
                                <i className='fas fa-upload me-2'></i>
                                Upload Workshop
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={isModalOpen} toggle={closeModal} backdrop='static' size='lg'>
                <form onSubmit={handleSubmit}>
                    <div className='modal-header'>
                        <h5 className='modal-title'>Previous Completed Training Workshop</h5>
                        <button type='button' className='btn-close' onClick={closeModal} aria-label='Close' />
                    </div>
                    <div className='modal-body'>
                        <FormGroup>
                            <Label for='completed-workshop-name'>
                                Training/Workshop Name <span className='text-danger'>*</span>
                            </Label>
                            <Input
                                id='completed-workshop-name'
                                type='select'
                                value={workshopCode}
                                onChange={(event) => setWorkshopCode(event.target.value)}
                                required
                            >
                                <option value=''>Select a training/workshop</option>
                                {COMPLETED_TRAINING_WORKSHOPS.map((workshop) => (
                                    <option key={workshop.code} value={workshop.code}>
                                        {workshop.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <Row>
                            <Col md='6'>
                                <FormGroup>
                                    <Label for='completed-workshop-start-date'>
                                        Start Date <span className='text-danger'>*</span>
                                    </Label>
                                    <Input
                                        id='completed-workshop-start-date'
                                        type='date'
                                        value={startDate}
                                        max={endDate || undefined}
                                        onChange={(event) => setStartDate(event.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md='6'>
                                <FormGroup>
                                    <Label for='completed-workshop-end-date'>
                                        End Date <span className='text-danger'>*</span>
                                    </Label>
                                    <Input
                                        id='completed-workshop-end-date'
                                        type='date'
                                        value={endDate}
                                        min={startDate || undefined}
                                        onChange={(event) => setEndDate(event.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup className='mb-0'>
                            <Label for='completed-workshop-document'>
                                Document <span className='text-danger'>*</span>
                            </Label>
                            <Input
                                key={fileInputKey}
                                id='completed-workshop-document'
                                type='file'
                                accept='.pdf,.jpg,.jpeg,.png'
                                onChange={(event) => setDocument(event.target.files?.[0] || null)}
                                required
                            />
                            <small className='text-muted'>Accepted formats: PDF, JPG, JPEG, PNG.</small>
                        </FormGroup>

                        <hr className='my-4' />
                        <h6 className='mb-3'>Previously Uploaded Workshops</h6>
                        {isWorkshopListLoading ? (
                            <p className='text-muted mb-0'>Loading uploaded workshops...</p>
                        ) : isWorkshopListError ? (
                            <Alert color='danger' className='mb-0'>Unable to load uploaded workshops.</Alert>
                        ) : completedWorkshops.length === 0 ? (
                            <p className='text-muted mb-0'>No workshop documents have been uploaded.</p>
                        ) : (
                            <div className='table-responsive'>
                                <Table bordered hover className='align-middle mb-0'>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}>#</th>
                                            <th>Training/Workshop Name</th>
                                            <th style={{ width: '130px' }}>Start Date</th>
                                            <th style={{ width: '130px' }}>End Date</th>
                                            <th style={{ width: '110px' }}>Document</th>
                                            <th style={{ width: '100px' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {completedWorkshops.map((workshop: any, index: number) => (
                                            <tr key={workshop?.id || index}>
                                                <td>{index + 1}</td>
                                                <td>{workshop?.training_workshop_name || workshop?.workshop_name || '-'}</td>
                                                <td>{workshop?.start_date || '-'}</td>
                                                <td>{workshop?.end_date || '-'}</td>
                                                <td>
                                                    {workshop?.document ? (
                                                        <a
                                                            href={getDocumentUrl(workshop.document)}
                                                            target='_blank'
                                                            rel='noreferrer'
                                                            className='btn btn-sm btn-outline-primary'
                                                        >
                                                            View
                                                        </a>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td>
                                                    <Button
                                                        type='button'
                                                        color='danger'
                                                        size='sm'
                                                        disabled={!workshop?.id}
                                                        onClick={() => setWorkshopIdToDelete(String(workshop.id))}
                                                    >
                                                        Delete
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                    <div className='modal-footer'>
                        <Button type='button' color='secondary' outline onClick={closeModal} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button type='submit' color='primary' disabled={isLoading}>
                            {isLoading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {workshopIdToDelete ? (
                <DeleteInformationModal
                    isOpen={Boolean(workshopIdToDelete)}
                    toggle={() => setWorkshopIdToDelete('')}
                    id={workshopIdToDelete}
                    deleteInfomation={handleDelete}
                />
            ) : null}
        </>
    );
};

const TraineeDashboard = (props: any) => {
    const { mainDashboard, personalProfile } = props;
    document.title = 'Trainee Dashboard | BCPS';

    const completeIcon = `<i class=" fas fa-check"></i>`;
    const defaultIcon = `<i class="fas fa-circle"></i>`;
    const youAreHereIcon = `<i class="fas fa-map-marker-alt"></i>`;

    const [timelineModalShow, setTimeLineModalShow] = useState<boolean>(false);
    const [viewDetailsModalShow, setViewDetailsModalShow] = useState<boolean>(false);
    const [reportId, setReportId] = useState('');
    const [showCoreTrainingCertificate, setShowCoreTrainingCertificate] = useState(false);
    const isMainCerticateEligible =
        String(personalProfile?.data?.core_certificate_status || '')
            .trim()
            .toLowerCase() === 'eligible';
    const isProvisionalCertificateApproved = !isMainCerticateEligible &&
        String(personalProfile?.data?.details?.provision_certificate_status || '')
            .trim()
            .toLowerCase() === 'approve';
    const hasCertificateApproval = isMainCerticateEligible ||
        String(personalProfile?.data?.details?.provision_certificate_status || '')
            .trim()
            .toLowerCase() === 'approve';

    const {
        data: traineeDashboardData,
        isSuccess: isTraineeDashboardDataSuccess,
        isError: isTraineeDashboardDataError,
        isLoading: isTraineeDashboardDataLoading,
        error: traineeDashboardDataError,
    } = useGetTraineeDashboardInfoQuery();

    var progressData = [
        {
            icon: `fas fa-book-open`,
            title: `FCPS Part-I`,
            isComplete: traineeDashboardData?.data?.fcps_part_1,
        },

        {
            icon: `fas fa-book-medical`,
            title: `FCPS Part-II`,
            isComplete: traineeDashboardData?.data?.fcps_part_2,
        },
        {
            icon: `fas fa-clinic-medical`,
            title: `Core Training Year-1`,
            isComplete: traineeDashboardData?.data?.core_training_year_1_slot_2,
        },

        {
            icon: `fas fa-clinic-medical`,
            title: `Core Training Year-2`,
            isComplete: traineeDashboardData?.data?.core_training_year_2_slot_2,
        },

        {
            icon: `fas fa-notes-medical`,
            title: `Mid-Term Exam`,
            isComplete: traineeDashboardData?.data?.mid_term_exam,
        },
        {
            icon: `fas fa-stethoscope`,
            title: `Advanced Training Year-1`,
            isComplete: traineeDashboardData?.data?.advance_training_year_1_slot_2,
        },

        {
            icon: `fas fa-stethoscope`,
            title: `Advanced Training Year-2`,
            isComplete: traineeDashboardData?.data?.advance_training_year_2,
        },
        {
            icon: `fas fa-stethoscope`,
            title: `Advanced Training Year-3`,
            isComplete: traineeDashboardData?.data?.advance_training_year_3,
        },


        {
            icon: ` fas fa-heartbeat`,
            title: `Submission of Thesis`,
            isComplete: traineeDashboardData?.data?.thesis_submission,
        },

        {
            icon: `fas fa-notes-medical`,
            title: `FCPS Final Exam`,
            isComplete: traineeDashboardData?.data?.['fcps-final-exam'],
        },
        {
            icon: ` fas fa-certificate`,
            title: `Fellowship`,
            isComplete: traineeDashboardData?.data?.fellowship,
        },


    ]

    var progressPercentage = 0;

    var thesisDissertationData = [
        {
            date: ``,
            title: `Title`,
            isComplete: traineeDashboardData?.data?.title_submission === 'active' ? true : false,
        },

        {
            date: ``,
            title: `Protocol`,
            isComplete: traineeDashboardData?.data?.protocol_submission === 'active' ? true : false,
        },

        {
            date: ``,
            title: `Thesis/Dissertation`,
            isComplete: traineeDashboardData?.data?.thesis_submission === 'active' ? true : false,
        },
    ];

    var thesisDissertationDataForTimelineModal = [
        {
            date: ``,
            title: `Title approved by <br/> Guide. Co-Guide`,
            isComplete: traineeDashboardData?.data?.thesis_title?.guide_approval_status === 'Approve' ? true : false,
        },

        {
            date: ``,
            title: `Protocol Submitted to<br/>SCI'S`,
            isComplete: traineeDashboardData?.data?.protocol_submission === 'active' ? true : false,
        },
        {
            date: ``,
            title: `Protocol Approved by<br/>BCPS`,
            isComplete: false,
        },

        {
            date: ``,
            title: `Thesis/Dissertation<br/>Submitted to BCPS`,
            isComplete: false,
        },

        {
            date: ``,
            title: `Thesis Defense:<br/>Passed/Failed`,
            isComplete: false,
        },
        {
            date: ``,
            title: `Dissertation Review:<br/>Accepted/Rejected`,
            isComplete: false,
        },
    ];


    var thesisDissertationPercentage = 0;
    var thesisDissertationDataForTimelineModalPercentage = 0;


    const Counter = ({ end, duration }: any) => {
        const [count, setCount] = useState<number>(0);

        useEffect(() => {
            let start = 0;
            const totalSteps = 60; // Number of updates in the given duration
            const increment = end / totalSteps;

            const stepTime = (duration * 1000) / totalSteps; // Step interval in ms

            const interval = setInterval(() => {
                start += increment;
                if (start >= end) {
                    start = end;
                    clearInterval(interval);
                }
                setCount(Math.floor(start));
            }, stepTime);

            return () => clearInterval(interval);
        }, [end, duration]);

        // Format number with separator
        const formatNumber = (num: number) => num.toLocaleString();

        return (
            <span>
                {formatNumber(count)}
            </span>
        );
    };

    return (
        <>
            {
                mainDashboard ?
                    <React.Fragment >
                        {isTraineeDashboardDataLoading ? (
                            <div className="overlay">
                                <img src={loader} alt="Loading" height={100} width={300} />
                            </div>
                        ) : null}
                        <div className='page-breadcrumb-wrapper'>
                            <BetterBreadcrumb title='Trainee Dashboard' />
                            <a
                                type='button'
                                rel='noreferrer'
                                className='btn btn-link text-decoration-underline'
                                href={'/documents/FCPS Part-I Passed Trainees Online Registration.pdf'}
                                target='_blank'
                            >
                                FCPS Part-2 Application Notice
                            </a>
                            {hasCertificateApproval ? <Button
                                type='button'
                                color='success'
                                size='sm'
                                onClick={() => setShowCoreTrainingCertificate(true)}
                            >
                                <i className='fas fa-file-pdf me-2'></i>
                                {isProvisionalCertificateApproved ? 'Provisional Core Training Certificate' : 'Core Training Certificate'}
                            </Button> : null}
                        </div>
                        {personalProfile?.data?.details?.bmdc_registration_no_verified ===
                            'Pending' ? (
                            <Alert color='danger'>Your BMDC Number is Under Review!</Alert>
                        ) : null}
                        {/* <br /> */}
                        {personalProfile?.data?.details?.profile_approval_status ===
                            'Submit' ? (
                            <Alert color='danger'> Your Registration Under Review! </Alert>
                        ) : null}
                        {personalProfile?.data?.details?.profile_approval_status ===
                            'Correction' ? (
                            <Alert color='danger'>
                                {' '}
                                Please Update Your Profile With Correct Information! {' '}{personalProfile?.data?.details?.profile_approval_note}
                            </Alert>
                        ) : null}
                        {personalProfile?.data?.details?.profile_approval_status ===
                            'Reject' ? (
                            <Alert color='danger'> Your Profile Rejected! </Alert>
                        ) : null}
                        <Row id='trainee-dashboard' style={personalProfile?.data?.details?.profile_approval_status !== 'Approve' ? { pointerEvents: 'none' } : {}}>
                            <Col md="12">
                                <Card className=''>
                                    <CardBody className='mx-1'>
                                        <div className="row g-3">
                                            {/* Row 1 - 6 buttons */}
                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-info bg-opacity-10 border border-primary text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => {
                                                        window.open(`/trainee/course-list`, '_blank');
                                                    }}
                                                >
                                                    <div className="text-primary mb-2">
                                                        <i className="fas fa-graduation-cap"></i>
                                                    </div>
                                                    <h5 className="text-primary fw-semibold mb-0">FCPS Apply</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-secondary bg-opacity-10 border border-secondary text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => {
                                                        window.open(`/trainee/trainee-registration-worklist`, '_blank');
                                                    }}
                                                >
                                                    <div className="text-secondary mb-2">
                                                        <i className="fas fa-chalkboard-teacher"></i>
                                                    </div>
                                                    <h5 className="text-secondary fw-semibold mb-0">My Training</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-success bg-opacity-10 border border-success text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => {
                                                        window.open(`/trainee/offline-payment-list`, '_blank');
                                                    }}
                                                >
                                                    <div className="text-success mb-2">
                                                        <i className="fas fa-credit-card"></i>
                                                    </div>
                                                    <h5 className="text-success fw-semibold mb-0">Payment List</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-danger bg-opacity-10 border border-danger text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => {
                                                        window.open(`/trainee/logbook-entry`, '_blank');
                                                    }}
                                                >
                                                    <div className="text-danger mb-2">
                                                        <i className="fas fa-book"></i>
                                                    </div>
                                                    <h5 className="text-danger fw-semibold mb-0">Log Entry</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-warning bg-opacity-10 border border-warning text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => {
                                                        window.open(`/trainee/logbook/academic-activities`, '_blank');
                                                    }}
                                                >
                                                    <div className="text-warning mb-2">
                                                        <i className="fas fa-tasks"></i>
                                                    </div>
                                                    <h5 className="text-warning fw-semibold mb-0">Academic Activity</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-info bg-opacity-10 border border-info text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => {
                                                        window.open(`/trainee/training-summary`, '_blank');
                                                    }}
                                                >
                                                    <div className="text-info mb-2">
                                                        <i className="fas fa-chart-line"></i>
                                                    </div>
                                                    <h5 className="text-info fw-semibold mb-0">Training Summary</h5>
                                                </div>
                                            </div>

                                            {/* Row 2 - 6 buttons */}
                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-dark bg-opacity-10 border border-dark text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => {
                                                        window.open(`/thesis-dissertation-title-and-protocol-management`, '_blank');
                                                    }}
                                                >
                                                    <div className="text-dark mb-2">
                                                        <i className="fas fa-file-alt"></i>
                                                    </div>
                                                    <h5 className="text-dark fw-semibold mb-0">Dissertation/Thesis</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-info bg-opacity-10 border border-primary text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => { }}
                                                >
                                                    <div className="text-primary mb-2">
                                                        <i className="fas fa-pencil-alt"></i>
                                                    </div>
                                                    <h5 className="text-primary fw-semibold mb-0">Exam</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-secondary bg-opacity-10 border border-secondary text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => { }}
                                                >
                                                    <div className="text-secondary mb-2">
                                                        <i className="fas fa-laptop"></i>
                                                    </div>
                                                    <h5 className="text-secondary fw-semibold mb-0">LMS</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-success bg-opacity-10 border border-success text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => { }}
                                                >
                                                    <div className="text-success mb-2">
                                                        <i className="fas fa-book-reader"></i>
                                                    </div>
                                                    <h5 className="text-success fw-semibold mb-0">E-Library</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-danger bg-opacity-10 border border-danger text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => { }}
                                                >
                                                    <div className="text-danger mb-2">
                                                        <i className="fas fa-bell"></i>
                                                    </div>
                                                    <h5 className="text-danger fw-semibold mb-0">BCPS Updates</h5>
                                                </div>
                                            </div>

                                            <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                <div
                                                    className="dashboard-card bg-warning bg-opacity-10 border border-warning text-center p-2 rounded-3"
                                                    role="button"
                                                    onClick={(e) => { }}
                                                >
                                                    <div className="text-warning mb-2">
                                                        <i className="fas fa-newspaper"></i>
                                                    </div>
                                                    <h5 className="text-warning fw-semibold mb-0">Journal</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col xl="12">
                                <PreviousCompletedTrainingWorkshop />

                                <Row>
                                    <Col xl="12">
                                        <Card className='progressContainer'>
                                            {/* <CardHeader>
                                    <CardTitle className="h5">Your Progress Map</CardTitle>
                                </CardHeader> */}
                                            <CardBody>
                                                <div className="progressData" >
                                                    <div className="position-relative progressWrapper " >


                                                        {progressData.map((data: any, key: number) => {

                                                            if (data.isComplete === 'active') {
                                                                progressPercentage = ((key / (progressData.length - 1) * 100));
                                                            }

                                                            return (
                                                                <div key={Math.random()} className="position-absolute top-0  translate-middle " style={{ left: (key / (progressData.length - 1) * 100) + "%" }}>
                                                                    <div className={data.isComplete === 'active' ?
                                                                        "bg-success thumbnail" :
                                                                        "bg-white thumbnail"}
                                                                    >
                                                                        <i className={data.icon} ></i>
                                                                    </div>

                                                                    <button
                                                                        className={data.isComplete === 'active' ? 'position-absolute top-0  translate-middle btn btn-sm btn-success rounded-pill' : 'position-absolute top-0  translate-middle btn btn-sm btn-secondary rounded-pill '}
                                                                        dangerouslySetInnerHTML={{ __html: data.isComplete === 'active' ? completeIcon : defaultIcon }}
                                                                    >
                                                                    </button>
                                                                    <div className="position-absolute translate-middle-x text-center  title " style={{ width: "100px", height: "auto", top: "30px" }}
                                                                        dangerouslySetInnerHTML={{ __html: data.title }}>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}

                                                        <div className="you-are-here-marker" style={{ left: progressPercentage + 6 + "%" }}>
                                                            <button
                                                                className={'position-absolute top-0  translate-middle btn btn-sm btn-success rounded-pill'}
                                                                dangerouslySetInnerHTML={{ __html: youAreHereIcon }}
                                                            >
                                                            </button>
                                                            <div className="info" >
                                                                You are<br />here
                                                            </div>
                                                        </div>

                                                        <Progress
                                                            striped
                                                            value={progressPercentage + 6}
                                                            color="success"
                                                            style={{ height: "4px" }}
                                                        ></Progress>
                                                    </div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="h5"><i className="fas fa-book"></i> Logbook Summary</CardTitle>
                                            </CardHeader>
                                            <CardBody>

                                                <Row className='mt-3'>
                                                    <Col lg={3} md={6} sm={6} xs={6} className='mt-3' >
                                                        <div className='bg-success text-white shadow-primary card-h-100 card '>
                                                            <div className="text-center p-2">
                                                                <i className="fas fa-stethoscope widget-box-1-icon"></i>
                                                                <div className="avatar-md m-auto position-absolute top-0 start-50 translate-middle">
                                                                    <span className="avatar-title rounded-circle bg-light-subtle text-white font-size-24">
                                                                        <i className="fas fa-stethoscope"></i>
                                                                    </span>
                                                                </div>
                                                                <h5 className="mt-4 lh-base fw-normal text-white font-size-18">
                                                                    <b>Patient</b><br />Management
                                                                </h5>
                                                                <h1 className="mt-0 lh-base fw-normal text-primary font-size-24 ">
                                                                    <b>
                                                                        <Counter end={traineeDashboardData?.data?.patient_management_count ?? 0} duration={1} />
                                                                    </b>
                                                                </h1>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg={3} md={6} sm={6} xs={6} className='mt-3'>
                                                        <div className='bg-dark text-white shadow-primary card-h-100 card '>
                                                            <div className="text-center p-2">
                                                                <i className=" fas fa-bed widget-box-1-icon"></i>
                                                                <div className="avatar-md m-auto position-absolute top-0 start-50 translate-middle">
                                                                    <span className="avatar-title rounded-circle bg-warning text-white font-size-24">
                                                                        <i className=" fas fa-bed"></i>
                                                                    </span>
                                                                </div>
                                                                <h5 className="mt-4 lh-base fw-normal text-white font-size-18">
                                                                    <b>Procedure</b><br />&nbsp;
                                                                </h5>
                                                                <h1 className="mt-0 lh-base fw-normal text-warning font-size-24 ">
                                                                    <b>
                                                                        <Counter end={traineeDashboardData?.data?.procedural_skill_count ?? 0} duration={1} />
                                                                    </b>
                                                                </h1>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg={3} md={6} sm={6} xs={6} className='mt-3'>
                                                        <div className='bg-primary text-white shadow-primary card-h-100 card '>
                                                            <div className="text-center p-2">
                                                                <i className="fas fa-stethoscope widget-box-1-icon"></i>
                                                                <div className="avatar-md m-auto position-absolute top-0 start-50 translate-middle">
                                                                    <span className="avatar-title rounded-circle bg-danger text-white font-size-24">
                                                                        <i className="fas fa-stethoscope"></i>
                                                                    </span>
                                                                </div>
                                                                <h5 className="mt-4 lh-base fw-normal text-white font-size-18">
                                                                    <b>Interpretation</b><br />&nbsp;
                                                                </h5>
                                                                <h1 className="mt-0 lh-base fw-normal text-danger font-size-24 ">
                                                                    <b>
                                                                        <Counter end={traineeDashboardData?.data?.interpretative_skill_count ?? 0} duration={1} />
                                                                    </b>
                                                                </h1>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg={3} md={6} sm={6} xs={6} className='mt-3'>
                                                        <div className='bg-warning text-white shadow-primary card-h-100 card '>
                                                            <div className="text-center p-2">
                                                                <i className=" fas fa-heartbeat widget-box-1-icon"></i>
                                                                <div className="avatar-md m-auto position-absolute top-0 start-50 translate-middle">
                                                                    <span className="avatar-title rounded-circle bg-success text-white font-size-24">
                                                                        <i className=" fas fa-heartbeat"></i>
                                                                    </span>
                                                                </div>
                                                                <h5 className="mt-4 lh-base fw-normal text-white font-size-18">
                                                                    <b>Academic</b><br />Activities
                                                                </h5>
                                                                <h1 className="mt-0 lh-base fw-normal text-success font-size-24 ">
                                                                    <b>
                                                                        <Counter end={traineeDashboardData?.data?.academic_activities_count ?? 0} duration={1} />
                                                                    </b>
                                                                </h1>
                                                            </div>
                                                        </div>
                                                    </Col>

                                                </Row>


                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md="6">
                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="h5"><i className=" fas fa-calendar-alt"></i> Upcoming Schedule</CardTitle>
                                            </CardHeader>
                                            <CardBody>
                                                <Table className="align-middle mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Event Name</th>
                                                            <th>Date</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    {/* <tbody>
                                                        <tr>
                                                            <th scope="row">1</th>
                                                            <td>Event 1</td>
                                                            <td>01-Jul-2023</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light btn-sm"
                                                                >
                                                                    View
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th scope="row">2</th>
                                                            <td>Event 2</td>
                                                            <td>05-Jul-2023</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light btn-sm"
                                                                >
                                                                    View
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th scope="row">3</th>
                                                            <td>Event 3</td>
                                                            <td>05-Sep-2023</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light btn-sm"
                                                                >
                                                                    View
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    </tbody> */}
                                                </Table>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* <Row>
                                    <Col md="12">
                                        <Card className='card-h-100'>
                                            <CardHeader>
                                                <CardTitle className="h5"><i className=" fas fa-tasks"></i> Logbook Target vs Achievement</CardTitle>
                                            </CardHeader>
                                            <CardBody>
                                                <BarChart />
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row> */}

                                <Row>
                                    <Col md={"12"}>
                                        <Card className='thesisDissertationContainer'>
                                            <CardHeader className='row align-items-center justify-content-between'>
                                                <Col md={7} sm={12}>
                                                    <CardTitle className="h5"><i className=" fas fa-notes-medical"></i>&nbsp;Dissertation/Thesis Status</CardTitle>
                                                    {traineeDashboardData?.data?.thesis_title?.title_of_thesis && <p className="card-text"><b>Title: </b>{traineeDashboardData?.data?.thesis_title?.title_of_thesis}</p>}
                                                </Col>
                                                <Col md={4} sm={12}>
                                                    <Button
                                                        type="button"
                                                        color='secondary'
                                                        className='me-2 mb-2'
                                                        onClick={(e) => {
                                                            setTimeLineModalShow(true);
                                                        }}>
                                                        <span className='fas fa-eye'></span> View timeline
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        color='primary'
                                                        className='me-2 mb-2'
                                                        onClick={(e) => {
                                                            setViewDetailsModalShow(true);
                                                        }}>
                                                        <span className='fas fa-eye'></span> View details
                                                    </Button>
                                                    {traineeDashboardData?.data?.thesis_result === 'passed' ?
                                                    <Button
                                                        type="button"
                                                        color='success'
                                                        className='mb-2'
                                                        outline
                                                        onClick={(e) => {
                                                            setReportId(traineeDashboardData?.data?.thesis_title?.id);
                                                        }}
                                                    >
                                                        Notification
                                                    </Button>: null}

                                                    {(reportId === traineeDashboardData?.data?.thesis_title?.id) ? (
                                                        <NotificationPdf
                                                            userInfo={personalProfile}
                                                            setReportId={setReportId}
                                                            reportId={reportId}
                                                            controllerInfo={traineeDashboardData?.data}
                                                        />
                                                    ) : null}
                                                </Col>
                                            </CardHeader>
                                            <CardBody>
                                                <Row>
                                                    <Col md={"12"}>
                                                        <div className="thesisDissertationData " >
                                                            <div className="position-relative thesisDissertationWrapper " >
                                                                {thesisDissertationData.map((data: any, key: number) => {

                                                                    if (data.isComplete) {
                                                                        thesisDissertationPercentage = ((key / (thesisDissertationData.length - 1) * 100));
                                                                    }

                                                                    return (
                                                                        <div key={Math.random()} className="position-absolute top-0 translate-middle" style={{ left: (key / (thesisDissertationData.length - 1) * 100) + "%" }}>


                                                                            {data.date && <div className={"thumbnail ms-5"}>{data.date}</div>}

                                                                            <button
                                                                                id="checkButton"
                                                                                className={data.isComplete ? 'position-absolute top-0 ms-5  translate-middle btn btn-sm btn-success rounded-pill' : 'position-absolute top-0 ms-5  translate-middle btn btn-sm btn-secondary rounded-pill'}
                                                                                dangerouslySetInnerHTML={{ __html: data.isComplete ? completeIcon : defaultIcon }}
                                                                            >
                                                                            </button>
                                                                            <div className="position-absolute translate-middle-x text-center"
                                                                                style={{ width: "150px", height: "100px", top: "30px", marginLeft: "55px" }}
                                                                                dangerouslySetInnerHTML={{ __html: data.title }}
                                                                            >
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })}


                                                                <Progress
                                                                    striped
                                                                    value={thesisDissertationPercentage}
                                                                    color="success"
                                                                    style={{ height: "4px", width: "1027px", marginLeft: "50px" }}
                                                                ></Progress>
                                                            </div>
                                                        </div>

                                                    </Col>
                                                    <Col md='12'>
                                                        <hr />
                                                        {/* <ol className='m-0 d-flex gap-5'>
                                                            <li>Start Date: 01-Jul-23</li>
                                                            <li>Study Period: 12 Months</li>
                                                            <li>Place of Study: DMCH</li>
                                                            <li>Guide: Prof. Dr. Rashedul Islam</li>
                                                            <li>Co-Guide: Asst. Prof. Dr. Kibria, Asst. Prof. Dr. Stranger</li>
                                                        </ol> */}

                                                    </Col>
                                                </Row>

                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Modal
                            onOpened={function () {
                                toast.dismiss();
                            }}
                            isOpen={timelineModalShow}
                            scrollable={true}
                            size='xl'
                            toggle={() => {
                                document.body.classList.add('no_padding');
                            }}
                            backdrop={'static'}
                            modalTransition={{ timeout: 0 }}
                            backdropTransition={{ timeout: 0 }}
                        >
                            <div className="modal-header">
                                <h5 className="modal-title" id="staticBackdropLabel">
                                    Dissertation/Thesis Timeline
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setTimeLineModalShow(!timelineModalShow);
                                    }}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                <Row id='trainee-dashboard'>
                                    <Row>
                                        <Col md={"12"}>
                                            <Card className='thesisDissertationContainer'>
                                                <CardHeader>
                                                    <CardTitle className="h5"><i className=" fas fa-notes-medical"></i>&nbsp;Dissertation/Thesis Status</CardTitle>
                                                    {traineeDashboardData?.data?.thesis_title?.title_of_thesis && <p className="card-text"><b>Title: </b>{traineeDashboardData?.data?.thesis_title?.title_of_thesis}</p>}
                                                </CardHeader>
                                                <CardBody>
                                                    <Row>
                                                        <Col md={"12"}>
                                                            <div className="thesisDissertationData " >
                                                                <div className="position-relative thesisDissertationWrapper " >
                                                                    {thesisDissertationDataForTimelineModal.map((data: any, key: number) => {

                                                                        if (data.isComplete) {
                                                                            thesisDissertationDataForTimelineModalPercentage = ((key / (thesisDissertationDataForTimelineModal.length - 1) * 100));
                                                                        }

                                                                        return (
                                                                            <div key={Math.random()} className="position-absolute top-0 translate-middle" style={{ left: (key / (thesisDissertationData.length - 1) * 100) + "%" }}>


                                                                                {data.date && <div className={"thumbnail ms-5"}>{data.date}</div>}

                                                                                <button
                                                                                    id="checkButton"
                                                                                    className={data.isComplete ? 'position-absolute top-0 ms-5  translate-middle btn btn-sm btn-success rounded-pill' : 'position-absolute top-0 ms-5  translate-middle btn btn-sm btn-secondary rounded-pill'}
                                                                                    dangerouslySetInnerHTML={{ __html: data.isComplete ? completeIcon : defaultIcon }}
                                                                                >
                                                                                </button>
                                                                                <div className="position-absolute translate-middle-x text-center"
                                                                                    style={{ width: "150px", height: "100px", top: "30px", marginLeft: "55px" }}
                                                                                    dangerouslySetInnerHTML={{ __html: data.title }}
                                                                                >
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    })}


                                                                    <Progress
                                                                        striped
                                                                        value={thesisDissertationDataForTimelineModalPercentage}
                                                                        color="success"
                                                                        style={{ height: "4px", width: "2170px", marginLeft: "50px" }}
                                                                    ></Progress>
                                                                </div>
                                                            </div>

                                                        </Col>
                                                        <Col md='12'>
                                                            <hr />
                                                            {/* <ol className='m-0 d-flex gap-5'>
                                                                <li>Start Date: 01-Jul-23</li>
                                                                <li>Study Period: 12 Months</li>
                                                                <li>Place of Study: DMCH</li>
                                                                <li>Guide: Prof. Dr. Rashedul Islam</li>
                                                                <li>Co-Guide: Asst. Prof. Dr. Kibria, Asst. Prof. Dr. Stranger</li>
                                                            </ol> */}

                                                        </Col>
                                                    </Row>

                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Row>
                            </div>
                        </Modal>

                        <Modal
                            onOpened={function () {
                                toast.dismiss();
                            }}
                            isOpen={viewDetailsModalShow}
                            scrollable={true}
                            size='xl'
                            toggle={() => {
                                document.body.classList.add('no_padding');
                            }}
                            backdrop={'static'}
                            modalTransition={{ timeout: 0 }}
                            backdropTransition={{ timeout: 0 }}
                        >
                            <div className="modal-header">
                                <h5 className="modal-title" id="staticBackdropLabel">
                                    Dissertation/Thesis Details
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setViewDetailsModalShow(!viewDetailsModalShow);
                                    }}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div>
                                    <h6 className="block-heading">Trainee Information</h6>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <table className="table table-bordered table-striped table-sm">
                                                <tbody>
                                                    <tr>
                                                        <td className="col-md-6 col-lg-6">
                                                            <strong>Trainee Name: </strong>
                                                            {traineeDashboardData?.data?.thesis_title?.trainee_name}
                                                        </td>
                                                        <td className="col-md-6 col-lg-6">
                                                            <strong>BMDC Reg. No: </strong>
                                                            {traineeDashboardData?.data?.thesis_title?.bmdc_registration_no}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <h6 className="block-heading">Dissertation/Thesis Details</h6>
                                    <div className="row">
                                        <div className='col-md-12'>
                                            <table className="table table-bordered table-striped table-sm">
                                                <tbody>
                                                    <tr>
                                                        <td className="col-md-6 col-lg-6">
                                                            <strong>Category: </strong>
                                                            {traineeDashboardData?.data?.thesis_title?.category}
                                                        </td>
                                                        <td className="col-md-6 col-lg-6">
                                                            <strong>Title: </strong>
                                                            {traineeDashboardData?.data?.thesis_title?.title_of_thesis}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <strong>Place Of Study: </strong>
                                                            {traineeDashboardData?.data?.thesis_title?.place_of_study ?? 'N/A'}
                                                        </td>
                                                        <td>
                                                            <strong>Study Period: </strong>
                                                            {traineeDashboardData?.data?.thesis_title?.study_period ?? ''} {traineeDashboardData?.data?.thesis_title?.study_period_unit ?? ''}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <strong>Guide: </strong>
                                                        </td>
                                                        <td>
                                                            <div className='row'>
                                                                <div className="col">
                                                                    <strong>Name:</strong>  {traineeDashboardData?.data?.thesis_title?.guide_name ?? 'N/A'} <br />
                                                                    <div className="d-flex gap-1 justify-content-start">
                                                                        <strong>Approval Status:</strong>
                                                                        <div>
                                                                            {traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'approve' && (
                                                                                <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                                                                                    {'Accepted'}
                                                                                </div>
                                                                            )}
                                                                            {traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'reject' && (
                                                                                <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                                                                                    {'Rejected'}
                                                                                </div>
                                                                            )}
                                                                            {traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'correction' && (
                                                                                <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                                                                                    {traineeDashboardData?.data?.thesis_title?.guide_approval_status}
                                                                                </div>
                                                                            )}
                                                                            {traineeDashboardData?.data?.thesis_title?.guide_approval_status !== 'Approve' &&
                                                                                traineeDashboardData?.data?.thesis_title?.guide_approval_status !== 'Reject' &&
                                                                                traineeDashboardData?.data?.thesis_title?.guide_approval_status !== 'Correction' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                                                                                        {traineeDashboardData?.data?.thesis_title?.guide_approval_status ?? 'Pending'}
                                                                                    </div>
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                    <strong>Approval Note: </strong>
                                                                    <span className={traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'approve' ? 'text-success' :
                                                                        traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'correction' ? 'text-warning' : 'text-danger'}
                                                                    >
                                                                        {traineeDashboardData?.data?.thesis_title?.guide_approval_note}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <strong>Co-Guide </strong>
                                                        </td>
                                                        <td>
                                                            {traineeDashboardData?.data?.thesis_title?.co_guide_1_name &&
                                                                <div className='row card mx-1 my-1'>
                                                                    <div className="col">
                                                                        <strong>Name:</strong>  {traineeDashboardData?.data?.thesis_title?.co_guide_1_name ?? 'N/A'} <br />
                                                                        <div className="d-flex gap-1 justify-content-start">
                                                                            <strong>Approval Status:</strong>
                                                                            <div>
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'approve' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                                                                                        {'Accepted'}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'reject' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                                                                                        {'Rejected'}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'correction' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                                                                                        {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status !== 'Approve' &&
                                                                                    traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status !== 'Reject' &&
                                                                                    traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status !== 'Correction' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status ?? 'Pending'}
                                                                                        </div>
                                                                                    )}
                                                                            </div>
                                                                        </div>
                                                                        <strong>Approval Note: </strong>
                                                                        <span className={traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'approve' ? 'text-success' :
                                                                            traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'correction' ? 'text-warning' : 'text-danger'}
                                                                        >
                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_note ?? 'N/A'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            }
                                                            {traineeDashboardData?.data?.thesis_title?.co_guide_2_name &&
                                                                <div className='row card mx-1 my-1'>
                                                                    <div className="col">
                                                                        <strong>Name:</strong>  {traineeDashboardData?.data?.thesis_title?.co_guide_2_name ?? 'N/A'} <br />
                                                                        <div className="d-flex gap-1 justify-content-start">
                                                                            <strong>Approval Status:</strong>
                                                                            <div>
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'approve' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                                                                                        {'Accepted'}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'reject' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                                                                                        {'Rejected'}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'correction' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                                                                                        {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status !== 'Approve' &&
                                                                                    traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status !== 'Reject' &&
                                                                                    traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status !== 'Correction' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status ?? 'Pending'}
                                                                                        </div>
                                                                                    )}
                                                                            </div>
                                                                        </div>
                                                                        <strong>Approval Note: </strong>
                                                                        <span className={traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'approve' ? 'text-success' :
                                                                            traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'correction' ? 'text-warning' : 'text-danger'}
                                                                        >
                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_note ?? 'N/A'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            }
                                                            {traineeDashboardData?.data?.thesis_title?.co_guide_3_name &&
                                                                <div className='row card mx-1 my-1'>
                                                                    <div className="col">
                                                                        <strong>Name:</strong>  {traineeDashboardData?.data?.thesis_title?.co_guide_3_name ?? 'N/A'} <br />
                                                                        <div className="d-flex gap-1 justify-content-start">
                                                                            <strong>Approval Status:</strong>
                                                                            <div>
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'approve' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                                                                                        {'Accepted'}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'reject' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                                                                                        {'Rejected'}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'correction' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                                                                                        {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status !== 'Approve' &&
                                                                                    traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status !== 'Reject' &&
                                                                                    traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status !== 'Correction' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status ?? 'Pending'}
                                                                                        </div>
                                                                                    )}
                                                                            </div>
                                                                        </div>
                                                                        <strong>Approval Note: </strong>
                                                                        <span className={traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'approve' ? 'text-success' :
                                                                            traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'correction' ? 'text-warning' : 'text-danger'}
                                                                        >
                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_note ?? 'N/A'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    </React.Fragment >
                    :
                    <div className='page-content' style={personalProfile?.data?.details?.profile_approval_status !== 'Approve' ? { pointerEvents: 'none' } : {}}>
                        {isTraineeDashboardDataLoading ? (
                            <div className="overlay">
                                <img src={loader} alt="Loading" height={100} width={300} />
                            </div>
                        ) : null}
                        <React.Fragment >
                            <div className='page-breadcrumb-wrapper'>
                                <BetterBreadcrumb title='Trainee Dashboard' />
                                {hasCertificateApproval ? <Button
                                    type='button'
                                    color='success'
                                    size='sm'
                                    onClick={() => setShowCoreTrainingCertificate(true)}
                                >
                                    <i className='fas fa-file-pdf me-2'></i>
                                    {isProvisionalCertificateApproved ? 'Provisional Core Training Certificate' : 'Core Training Certificate'}
                                </Button> : null}
                            </div>
                            {personalProfile?.data?.details?.bmdc_registration_no_verified ===
                                'Pending' ? (
                                <Alert color='danger'>Your BMDC Number is Under Review!</Alert>
                            ) : null}
                            {/* <br /> */}
                            {personalProfile?.data?.details?.profile_approval_status ===
                                'Submit' ? (
                                <Alert color='danger'> Your Registration Under Review! </Alert>
                            ) : null}
                            {personalProfile?.data?.details?.profile_approval_status ===
                                'Correction' ? (
                                <Alert color='danger'>
                                    {' '}
                                    Please Update Your Profile With Correct Information! {' '}{personalProfile?.data?.details?.profile_approval_note}
                                </Alert>
                            ) : null}
                            {personalProfile?.data?.details?.profile_approval_status ===
                                'Reject' ? (
                                <Alert color='danger'> Your Profile Rejected! </Alert>
                            ) : null}
                            <Row id='trainee-dashboard'>
                                <Col md="12">
                                    <Card className=''>
                                        <CardBody className='mx-1'>
                                            <div className="row g-3">
                                                {/* Row 1 - 6 buttons */}
                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-info bg-opacity-10 border border-primary text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => {
                                                            window.open(`/trainee/course-list`, '_blank');
                                                        }}
                                                    >
                                                        <div className="text-primary mb-2">
                                                            <i className="fas fa-graduation-cap"></i>
                                                        </div>
                                                        <h5 className="text-primary fw-semibold mb-0">FCPS Apply</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-secondary bg-opacity-10 border border-secondary text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => {
                                                            window.open(`/trainee/trainee-registration-worklist`, '_blank');
                                                        }}
                                                    >
                                                        <div className="text-secondary mb-2">
                                                            <i className="fas fa-chalkboard-teacher"></i>
                                                        </div>
                                                        <h5 className="text-secondary fw-semibold mb-0">My Training</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-success bg-opacity-10 border border-success text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => {
                                                            window.open(`/trainee/offline-payment-list`, '_blank');
                                                        }}
                                                    >
                                                        <div className="text-success mb-2">
                                                            <i className="fas fa-credit-card"></i>
                                                        </div>
                                                        <h5 className="text-success fw-semibold mb-0">Payment List</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-danger bg-opacity-10 border border-danger text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => {
                                                            window.open(`/trainee/logbook-entry`, '_blank');
                                                        }}
                                                    >
                                                        <div className="text-danger mb-2">
                                                            <i className="fas fa-book"></i>
                                                        </div>
                                                        <h5 className="text-danger fw-semibold mb-0">Log Entry</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-warning bg-opacity-10 border border-warning text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => {
                                                            window.open(`/trainee/logbook/academic-activities`, '_blank');
                                                        }}
                                                    >
                                                        <div className="text-warning mb-2">
                                                            <i className="fas fa-tasks"></i>
                                                        </div>
                                                        <h5 className="text-warning fw-semibold mb-0">Academic Activity</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-info bg-opacity-10 border border-info text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => {
                                                            window.open(`/trainee/training-summary`, '_blank');
                                                        }}
                                                    >
                                                        <div className="text-info mb-2">
                                                            <i className="fas fa-chart-line"></i>
                                                        </div>
                                                        <h5 className="text-info fw-semibold mb-0">Training Summary</h5>
                                                    </div>
                                                </div>

                                                {/* Row 2 - 6 buttons */}
                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-dark bg-opacity-10 border border-dark text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => {
                                                            window.open(`/thesis-dissertation-title-and-protocol-management`, '_blank');
                                                        }}
                                                    >
                                                        <div className="text-dark mb-2">
                                                            <i className="fas fa-file-alt"></i>
                                                        </div>
                                                        <h5 className="text-dark fw-semibold mb-0">Dissertation/Thesis</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-info bg-opacity-10 border border-primary text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => { }}
                                                    >
                                                        <div className="text-primary mb-2">
                                                            <i className="fas fa-pencil-alt"></i>
                                                        </div>
                                                        <h5 className="text-primary fw-semibold mb-0">Exam</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-secondary bg-opacity-10 border border-secondary text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => { }}
                                                    >
                                                        <div className="text-secondary mb-2">
                                                            <i className="fas fa-laptop"></i>
                                                        </div>
                                                        <h5 className="text-secondary fw-semibold mb-0">LMS</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-success bg-opacity-10 border border-success text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => { }}
                                                    >
                                                        <div className="text-success mb-2">
                                                            <i className="fas fa-book-reader"></i>
                                                        </div>
                                                        <h5 className="text-success fw-semibold mb-0">E-Library</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-danger bg-opacity-10 border border-danger text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => { }}
                                                    >
                                                        <div className="text-danger mb-2">
                                                            <i className="fas fa-bell"></i>
                                                        </div>
                                                        <h5 className="text-danger fw-semibold mb-0">BCPS Updates</h5>
                                                    </div>
                                                </div>

                                                <div className="col-6 col-sm-4 col-md-4 col-lg-2">
                                                    <div
                                                        className="dashboard-card bg-warning bg-opacity-10 border border-warning text-center p-2 rounded-3"
                                                        role="button"
                                                        onClick={(e) => { }}
                                                    >
                                                        <div className="text-warning mb-2">
                                                            <i className="fas fa-newspaper"></i>
                                                        </div>
                                                        <h5 className="text-warning fw-semibold mb-0">Journal</h5>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col xl="12">
                                    <PreviousCompletedTrainingWorkshop />

                                    <Row>
                                        <Col xl="12">
                                            <Card className='progressContainer'>
                                                {/* <CardHeader>
                                    <CardTitle className="h5">Your Progress Map</CardTitle>
                                </CardHeader> */}
                                                <CardBody>
                                                    <div className="progressData" >
                                                        <div className="position-relative progressWrapper " >


                                                            {progressData.map((data: any, key: number) => {

                                                                if (data.isComplete === 'active') {
                                                                    progressPercentage = ((key / (progressData.length - 1) * 100));
                                                                }

                                                                return (
                                                                    <div key={Math.random()} className="position-absolute top-0  translate-middle " style={{ left: (key / (progressData.length - 1) * 100) + "%" }}>
                                                                        <div className={data.isComplete === 'active' ?
                                                                            "bg-success thumbnail" :
                                                                            "bg-white thumbnail"}
                                                                        >
                                                                            <i className={data.icon} ></i>
                                                                        </div>

                                                                        <button
                                                                            className={data.isComplete === 'active' ? 'position-absolute top-0  translate-middle btn btn-sm btn-success rounded-pill' : 'position-absolute top-0  translate-middle btn btn-sm btn-secondary rounded-pill '}
                                                                            dangerouslySetInnerHTML={{ __html: data.isComplete === 'active' ? completeIcon : defaultIcon }}
                                                                        >
                                                                        </button>
                                                                        <div className="position-absolute translate-middle-x text-center  title " style={{ width: "100px", height: "auto", top: "30px" }}
                                                                            dangerouslySetInnerHTML={{ __html: data.title }}>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })}

                                                            <div className="you-are-here-marker" style={{ left: progressPercentage + 6 + "%" }}>
                                                                <button
                                                                    className={'position-absolute top-0  translate-middle btn btn-sm btn-success rounded-pill'}
                                                                    dangerouslySetInnerHTML={{ __html: youAreHereIcon }}
                                                                >
                                                                </button>
                                                                <div className="info" >
                                                                    You are<br />here
                                                                </div>
                                                            </div>

                                                            <Progress
                                                                striped
                                                                value={progressPercentage + 6}
                                                                color="success"
                                                                style={{ height: "4px" }}
                                                            ></Progress>
                                                        </div>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md="6">
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle className="h5"><i className="fas fa-book"></i> Logbook Summary</CardTitle>
                                                </CardHeader>
                                                <CardBody>

                                                    <Row className='mt-3'>
                                                        <Col lg={3} md={6} sm={6} xs={6} className='mt-3' >
                                                            <div className='bg-success text-white shadow-primary card-h-100 card '>
                                                                <div className="text-center p-2">
                                                                    <i className="fas fa-stethoscope widget-box-1-icon"></i>
                                                                    <div className="avatar-md m-auto position-absolute top-0 start-50 translate-middle">
                                                                        <span className="avatar-title rounded-circle bg-light-subtle text-white font-size-24">
                                                                            <i className="fas fa-stethoscope"></i>
                                                                        </span>
                                                                    </div>
                                                                    <h5 className="mt-4 lh-base fw-normal text-white font-size-18">
                                                                        <b>Patient</b><br />Management
                                                                    </h5>
                                                                    <h1 className="mt-0 lh-base fw-normal text-primary font-size-24 ">
                                                                        <b>
                                                                            <Counter end={traineeDashboardData?.data?.patient_management_count ?? 0} duration={1} />
                                                                        </b>
                                                                    </h1>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        <Col lg={3} md={6} sm={6} xs={6} className='mt-3'>
                                                            <div className='bg-dark text-white shadow-primary card-h-100 card '>
                                                                <div className="text-center p-2">
                                                                    <i className=" fas fa-bed widget-box-1-icon"></i>
                                                                    <div className="avatar-md m-auto position-absolute top-0 start-50 translate-middle">
                                                                        <span className="avatar-title rounded-circle bg-warning text-white font-size-24">
                                                                            <i className=" fas fa-bed"></i>
                                                                        </span>
                                                                    </div>
                                                                    <h5 className="mt-4 lh-base fw-normal text-white font-size-18">
                                                                        <b>Procedure</b><br />&nbsp;
                                                                    </h5>
                                                                    <h1 className="mt-0 lh-base fw-normal text-warning font-size-24 ">
                                                                        <b>
                                                                            <Counter end={traineeDashboardData?.data?.procedural_skill_count ?? 0} duration={1} />
                                                                        </b>
                                                                    </h1>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        <Col lg={3} md={6} sm={6} xs={6} className='mt-3'>
                                                            <div className='bg-primary text-white shadow-primary card-h-100 card '>
                                                                <div className="text-center p-2">
                                                                    <i className="fas fa-stethoscope widget-box-1-icon"></i>
                                                                    <div className="avatar-md m-auto position-absolute top-0 start-50 translate-middle">
                                                                        <span className="avatar-title rounded-circle bg-danger text-white font-size-24">
                                                                            <i className="fas fa-stethoscope"></i>
                                                                        </span>
                                                                    </div>
                                                                    <h5 className="mt-4 lh-base fw-normal text-white font-size-18">
                                                                        <b>Interpretation</b><br />&nbsp;
                                                                    </h5>
                                                                    <h1 className="mt-0 lh-base fw-normal text-danger font-size-24 ">
                                                                        <b>
                                                                            <Counter end={traineeDashboardData?.data?.interpretative_skill_count ?? 0} duration={1} />
                                                                        </b>
                                                                    </h1>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        <Col lg={3} md={6} sm={6} xs={6} className='mt-3'>
                                                            <div className='bg-warning text-white shadow-primary card-h-100 card '>
                                                                <div className="text-center p-2">
                                                                    <i className=" fas fa-heartbeat widget-box-1-icon"></i>
                                                                    <div className="avatar-md m-auto position-absolute top-0 start-50 translate-middle">
                                                                        <span className="avatar-title rounded-circle bg-success text-white font-size-24">
                                                                            <i className=" fas fa-heartbeat"></i>
                                                                        </span>
                                                                    </div>
                                                                    <h5 className="mt-4 lh-base fw-normal text-white font-size-18">
                                                                        <b>Academic</b><br />Activities
                                                                    </h5>
                                                                    <h1 className="mt-0 lh-base fw-normal text-success font-size-24 ">
                                                                        <b>
                                                                            <Counter end={traineeDashboardData?.data?.academic_activities_count ?? 0} duration={1} />
                                                                        </b>
                                                                    </h1>
                                                                </div>
                                                            </div>
                                                        </Col>

                                                    </Row>


                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col md="6">
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle className="h5"><i className=" fas fa-calendar-alt"></i> Upcoming Schedule</CardTitle>
                                                </CardHeader>
                                                <CardBody>
                                                    <Table className="align-middle mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Event Name</th>
                                                                <th>Date</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </thead>
                                                        {/* <tbody>
                                                            <tr>
                                                                <th scope="row">1</th>
                                                                <td>Event 1</td>
                                                                <td>01-Jul-2023</td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light btn-sm"
                                                                    >
                                                                        View
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th scope="row">2</th>
                                                                <td>Event 2</td>
                                                                <td>05-Jul-2023</td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light btn-sm"
                                                                    >
                                                                        View
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th scope="row">3</th>
                                                                <td>Event 3</td>
                                                                <td>05-Sep-2023</td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light btn-sm"
                                                                    >
                                                                        View
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        </tbody> */}
                                                    </Table>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* <Row>
                                        <Col md="12">
                                            <Card className='card-h-100'>
                                                <CardHeader>
                                                    <CardTitle className="h5"><i className=" fas fa-tasks"></i> Logbook Target vs Achievement</CardTitle>
                                                </CardHeader>
                                                <CardBody>
                                                    <BarChart />
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row> */}

                                    <Row>
                                        <Col md={"12"}>
                                            <Card className='thesisDissertationContainer'>
                                                <CardHeader className='row align-items-center justify-content-between'>
                                                    <Col md={7} sm={12}>
                                                        <CardTitle className="h5"><i className=" fas fa-notes-medical"></i>&nbsp;Dissertation/Thesis Status</CardTitle>
                                                        {traineeDashboardData?.data?.thesis_title?.title_of_thesis && <p className="card-text"><b>Title: </b>{traineeDashboardData?.data?.thesis_title?.title_of_thesis}</p>}

                                                    </Col>
                                                    <Col md={4} sm={12}>
                                                        <Button
                                                            type="button"
                                                            color='secondary'
                                                            className='me-2 mb-2'
                                                            onClick={(e) => {
                                                                setTimeLineModalShow(true);
                                                            }}>
                                                            <span className='fas fa-eye'></span> View timeline
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            color='primary'
                                                            className='mb-2'
                                                            onClick={(e) => {
                                                                setViewDetailsModalShow(true);
                                                            }}>
                                                            <span className='fas fa-eye'></span> View details
                                                        </Button>
                                                    </Col>
                                                </CardHeader>
                                                <CardBody>
                                                    <Row>
                                                        <Col md={"12"}>
                                                            <div className="thesisDissertationData " >
                                                                <div className="position-relative thesisDissertationWrapper " >
                                                                    {thesisDissertationData.map((data: any, key: number) => {

                                                                        if (data.isComplete) {
                                                                            thesisDissertationPercentage = ((key / (thesisDissertationData.length - 1) * 100));
                                                                        }

                                                                        return (
                                                                            <div key={Math.random()} className="position-absolute top-0 translate-middle" style={{ left: (key / (thesisDissertationData.length - 1) * 100) + "%" }}>


                                                                                {data.date && <div className={"thumbnail ms-5"}>{data.date}</div>}

                                                                                <button
                                                                                    id="checkButton"
                                                                                    className={data.isComplete ? 'position-absolute top-0 ms-5  translate-middle btn btn-sm btn-success rounded-pill' : 'position-absolute top-0 ms-5  translate-middle btn btn-sm btn-secondary rounded-pill'}
                                                                                    dangerouslySetInnerHTML={{ __html: data.isComplete ? completeIcon : defaultIcon }}
                                                                                >
                                                                                </button>
                                                                                <div className="position-absolute translate-middle-x text-center"
                                                                                    style={{ width: "150px", height: "100px", top: "30px", marginLeft: "55px" }}
                                                                                    dangerouslySetInnerHTML={{ __html: data.title }}
                                                                                >
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    })}


                                                                    <Progress
                                                                        striped
                                                                        value={thesisDissertationPercentage}
                                                                        color="success"
                                                                        style={{ height: "4px", width: "1180px", marginLeft: "50px" }}
                                                                    ></Progress>
                                                                </div>
                                                            </div>

                                                        </Col>
                                                        <Col md='12'>
                                                            <hr />
                                                            {/* <ol className='m-0 d-flex gap-5'>
                                                                <li>Start Date: 01-Jul-23</li>
                                                                <li>Study Period: 12 Months</li>
                                                                <li>Place of Study: DMCH</li>
                                                                <li>Guide: Prof. Dr. Rashedul Islam</li>
                                                                <li>Co-Guide: Asst. Prof. Dr. Kibria, Asst. Prof. Dr. Stranger</li>
                                                            </ol> */}

                                                        </Col>
                                                    </Row>

                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            <Modal
                                onOpened={function () {
                                    toast.dismiss();
                                }}
                                isOpen={timelineModalShow}
                                scrollable={true}
                                size='xl'
                                toggle={() => {
                                    document.body.classList.add('no_padding');
                                }}
                                backdrop={'static'}
                                modalTransition={{ timeout: 0 }}
                                backdropTransition={{ timeout: 0 }}
                            >
                                <div className="modal-header">
                                    <h5 className="modal-title" id="staticBackdropLabel">
                                        Dissertation/Thesis Timeline
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setTimeLineModalShow(!timelineModalShow);
                                        }}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <Row id='trainee-dashboard'>
                                        <Row>
                                            <Col md={"12"}>
                                                <Card className='thesisDissertationContainer'>
                                                    <CardHeader>
                                                        <CardTitle className="h5"><i className=" fas fa-notes-medical"></i>&nbsp;Dissertation/Thesis Status</CardTitle>
                                                        {traineeDashboardData?.data?.thesis_title?.title_of_thesis && <p className="card-text"><b>Title: </b>{traineeDashboardData?.data?.thesis_title?.title_of_thesis}</p>}

                                                    </CardHeader>
                                                    <CardBody>
                                                        <Row>
                                                            <Col md={"12"}>
                                                                <div className="thesisDissertationData " >
                                                                    <div className="position-relative thesisDissertationWrapper " >
                                                                        {thesisDissertationDataForTimelineModal.map((data: any, key: number) => {

                                                                            if (data.isComplete) {
                                                                                thesisDissertationPercentage = ((key / (thesisDissertationData.length - 1) * 100));
                                                                            }

                                                                            return (
                                                                                <div key={Math.random()} className="position-absolute top-0 translate-middle" style={{ left: (key / (thesisDissertationData.length - 1) * 100) + "%" }}>


                                                                                    {data.date && <div className={"thumbnail ms-5"}>{data.date}</div>}

                                                                                    <button
                                                                                        id="checkButton"
                                                                                        className={data.isComplete ? 'position-absolute top-0 ms-5  translate-middle btn btn-sm btn-success rounded-pill' : 'position-absolute top-0 ms-5  translate-middle btn btn-sm btn-secondary rounded-pill'}
                                                                                        dangerouslySetInnerHTML={{ __html: data.isComplete ? completeIcon : defaultIcon }}
                                                                                    >
                                                                                    </button>
                                                                                    <div className="position-absolute translate-middle-x text-center"
                                                                                        style={{ width: "150px", height: "100px", top: "30px", marginLeft: "55px" }}
                                                                                        dangerouslySetInnerHTML={{ __html: data.title }}
                                                                                    >
                                                                                    </div>
                                                                                </div>
                                                                            )
                                                                        })}


                                                                        <Progress
                                                                            striped
                                                                            value={thesisDissertationPercentage}
                                                                            color="success"
                                                                            style={{ height: "4px", width: "1180px", marginLeft: "50px" }}
                                                                        ></Progress>
                                                                    </div>
                                                                </div>

                                                            </Col>
                                                            <Col md='12'>
                                                                <hr />
                                                                {/* <ol className='m-0 d-flex gap-5'>
                                                                    <li>Start Date: 01-Jul-23</li>
                                                                    <li>Study Period: 12 Months</li>
                                                                    <li>Place of Study: DMCH</li>
                                                                    <li>Guide: Prof. Dr. Rashedul Islam</li>
                                                                    <li>Co-Guide: Asst. Prof. Dr. Kibria, Asst. Prof. Dr. Stranger</li>
                                                                </ol> */}

                                                            </Col>
                                                        </Row>

                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </Row>
                                </div>
                            </Modal>

                            <Modal
                                onOpened={function () {
                                    toast.dismiss();
                                }}
                                isOpen={viewDetailsModalShow}
                                scrollable={true}
                                size='xl'
                                toggle={() => {
                                    document.body.classList.add('no_padding');
                                }}
                                backdrop={'static'}
                                modalTransition={{ timeout: 0 }}
                                backdropTransition={{ timeout: 0 }}
                            >
                                <div className="modal-header">
                                    <h5 className="modal-title" id="staticBackdropLabel">
                                        Dissertation/Thesis Details
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setViewDetailsModalShow(!viewDetailsModalShow);
                                        }}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div>
                                        <h6 className="block-heading">Trainee Information</h6>
                                        <div className="row">
                                            <div className="col-md-12">
                                                <table className="table table-bordered table-striped table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td className="col-md-6 col-lg-6">
                                                                <strong>Trainee Name: </strong>
                                                                {traineeDashboardData?.data?.thesis_title?.trainee_name}
                                                            </td>
                                                            <td className="col-md-6 col-lg-6">
                                                                <strong>BMDC Reg. No: </strong>
                                                                {traineeDashboardData?.data?.thesis_title?.bmdc_registration_no}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <h6 className="block-heading">Dissertation/Thesis Details</h6>
                                        <div className="row">
                                            <div className='col-md-12'>
                                                <table className="table table-bordered table-striped table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td className="col-md-6 col-lg-6">
                                                                <strong>Category: </strong>
                                                                {traineeDashboardData?.data?.thesis_title?.category}
                                                            </td>
                                                            <td className="col-md-6 col-lg-6">
                                                                <strong>Title: </strong>
                                                                {traineeDashboardData?.data?.thesis_title?.title_of_thesis}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <strong>Place Of Study: </strong>
                                                                {traineeDashboardData?.data?.thesis_title?.place_of_study ?? 'N/A'}
                                                            </td>
                                                            <td>
                                                                <strong>Study Period: </strong>
                                                                {traineeDashboardData?.data?.thesis_title?.study_period ?? 'N/A'} {traineeDashboardData?.data?.thesis_title?.study_period_unit ?? ''}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <strong>Guide: </strong>
                                                            </td>
                                                            <td>
                                                                <div className='row'>
                                                                    <div className="col">
                                                                        <strong>Name:</strong>  {traineeDashboardData?.data?.thesis_title?.guide_name ?? 'N/A'} <br />
                                                                        <div className="d-flex gap-1 justify-content-start">
                                                                            <strong>Approval Status:</strong>
                                                                            <div>
                                                                                {traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'approve' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                                                                                        {'Accepted'}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'reject' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                                                                                        {'Rejected'}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'correction' && (
                                                                                    <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                                                                                        {traineeDashboardData?.data?.thesis_title?.guide_approval_status}
                                                                                    </div>
                                                                                )}
                                                                                {traineeDashboardData?.data?.thesis_title?.guide_approval_status !== 'Approve' &&
                                                                                    traineeDashboardData?.data?.thesis_title?.guide_approval_status !== 'Reject' &&
                                                                                    traineeDashboardData?.data?.thesis_title?.guide_approval_status !== 'Correction' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                                                                                            {traineeDashboardData?.data?.thesis_title?.guide_approval_status ?? 'Pending'}
                                                                                        </div>
                                                                                    )}
                                                                            </div>
                                                                        </div>
                                                                        <strong>Approval Note: </strong>
                                                                        <span className={traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'approve' ? 'text-success' :
                                                                            traineeDashboardData?.data?.thesis_title?.guide_approval_status?.toLowerCase() === 'correction' ? 'text-warning' : 'text-danger'}
                                                                        >
                                                                            {traineeDashboardData?.data?.thesis_title?.guide_approval_note}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <strong>Co-Guide </strong>
                                                            </td>
                                                            <td>
                                                                {traineeDashboardData?.data?.thesis_title?.co_guide_1_name &&
                                                                    <div className='row card mx-1 my-1'>
                                                                        <div className="col">
                                                                            <strong>Name:</strong>  {traineeDashboardData?.data?.thesis_title?.co_guide_1_name ?? 'N/A'} <br />
                                                                            <div className="d-flex gap-1 justify-content-start">
                                                                                <strong>Approval Status:</strong>
                                                                                <div>
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'approve' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                                                                                            {'Accepted'}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'reject' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                                                                                            {'Rejected'}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'correction' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status !== 'Approve' &&
                                                                                        traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status !== 'Reject' &&
                                                                                        traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status !== 'Correction' && (
                                                                                            <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status ?? 'Pending'}
                                                                                            </div>
                                                                                        )}
                                                                                </div>
                                                                            </div>
                                                                            <strong>Approval Note: </strong>
                                                                            <span className={traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'approve' ? 'text-success' :
                                                                                traineeDashboardData?.data?.thesis_title?.co_guide1_approval_status?.toLowerCase() === 'correction' ? 'text-warning' : 'text-danger'}
                                                                            >
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide1_approval_note ?? 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                }
                                                                {traineeDashboardData?.data?.thesis_title?.co_guide_2_name &&
                                                                    <div className='row card mx-1 my-1'>
                                                                        <div className="col">
                                                                            <strong>Name:</strong>  {traineeDashboardData?.data?.thesis_title?.co_guide_2_name ?? 'N/A'} <br />
                                                                            <div className="d-flex gap-1 justify-content-start">
                                                                                <strong>Approval Status:</strong>
                                                                                <div>
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'approve' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                                                                                            {'Accepted'}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'reject' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                                                                                            {'Rejected'}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'correction' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status !== 'Approve' &&
                                                                                        traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status !== 'Reject' &&
                                                                                        traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status !== 'Correction' && (
                                                                                            <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status ?? 'Pending'}
                                                                                            </div>
                                                                                        )}
                                                                                </div>
                                                                            </div>
                                                                            <strong>Approval Note: </strong>
                                                                            <span className={traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'approve' ? 'text-success' :
                                                                                traineeDashboardData?.data?.thesis_title?.co_guide2_approval_status?.toLowerCase() === 'correction' ? 'text-warning' : 'text-danger'}
                                                                            >
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide2_approval_note ?? 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                }
                                                                {traineeDashboardData?.data?.thesis_title?.co_guide_3_name &&
                                                                    <div className='row card mx-1 my-1'>
                                                                        <div className="col">
                                                                            <strong>Name:</strong>  {traineeDashboardData?.data?.thesis_title?.co_guide_3_name ?? 'N/A'} <br />
                                                                            <div className="d-flex gap-1 justify-content-start">
                                                                                <strong>Approval Status:</strong>
                                                                                <div>
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'approve' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                                                                                            {'Accepted'}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'reject' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                                                                                            {'Rejected'}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'correction' && (
                                                                                        <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                                                                                            {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status}
                                                                                        </div>
                                                                                    )}
                                                                                    {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status !== 'Approve' &&
                                                                                        traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status !== 'Reject' &&
                                                                                        traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status !== 'Correction' && (
                                                                                            <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status ?? 'Pending'}
                                                                                            </div>
                                                                                        )}
                                                                                </div>
                                                                            </div>
                                                                            <strong>Approval Note: </strong>
                                                                            <span className={traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'approve' ? 'text-success' :
                                                                                traineeDashboardData?.data?.thesis_title?.co_guide3_approval_status?.toLowerCase() === 'correction' ? 'text-warning' : 'text-danger'}
                                                                            >
                                                                                {traineeDashboardData?.data?.thesis_title?.co_guide3_approval_note ?? 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                }
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        </React.Fragment >
                    </div>
            }
            {showCoreTrainingCertificate ? (
                <CoreTrainingCertificate
                    personalProfile={personalProfile}
                    onClose={() => setShowCoreTrainingCertificate(false)}
                />
            ) : null}
        </>
    );
};

export default TraineeDashboard;
